import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer, Provider } from 'mobx-react';
import styled, { ThemeProvider } from 'styled-components';
import globalStyles from './globalStyles';
import MediaQuery from "react-responsive";
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { Spinner } from '@kurtosys/ksys-portal-components/build/components/UI/Spinner.js';
import { DataStore, FilterStore } from './stores';
import Header from './Components/Header';
import AppLoading from './Components/AppLoading';
import Disclaimers from './Components/Disclaimers';
import EmptyState from './Components/EmptyState';
import Filters from './Components/Filters';
import FiltersTop from './Components/FiltersTop';
import Fund from './Components/Fund';
import FundTypeTabs from './Components/FundTypeTabs';
import ModuleTabs from './Components/ModuleTabs';
import LoadingOverlay from './Components/LoadingOverlay';
import LoadMoreButton from './Components/LoadMoreButton';
import Translate from './Components/Translate';
import DocDone from './Components/DocDone';
import get from 'lodash/get';

interface AppProps {
    intlSupported?: boolean;
    culture?: string;
    fundlist?: string;
    module?: string;
    scriptSrc?: string;
}

const SearchHeader = styled<any, any>('header')`
    ${propStyles};
    top: 0;
`;

SearchHeader.defaultProps = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    borderWidth: "1px",
    borderColor: "gray300",
    borderStyle: "none none solid none",
    padding: "lg"
};

const Wrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Wrapper.defaultProps = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    marginX: "auto",
    fontFamily: "body",
    fontSize: "sm",
    color: "body"
};

const Main = styled<any, any>('main')`
    ${propStyles};
    position: relative;
    z-index: 1;
`;

Main.defaultProps = {
    display: "flex",
    flexDirection: "column",
    margin: ["0", "0", "0 0 0 240px"]
};

const FundsWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FundsWrapper.defaultProps = {
    flex: "1 0 auto",
    display: "flex",
    flexDirection: "column"
};

@observer
class App extends Component<AppProps, any> {
    dataStore: any;
    filterStore: any;

    constructor(props: AppProps) {
        super(props);

        this.dataStore = new DataStore(props);
        this.filterStore = new FilterStore(this.dataStore);

        // Add global styles with injectGlobal
        globalStyles(this.dataStore.apiUrl);
    }

    render() {
        const { dataStore, filterStore } = this;

        return (
            dataStore.isBootstrapped ?
                (
                    <Provider
                        dataStore={dataStore}
                        filterStore={filterStore}
                        translations={dataStore.translations}
                    >
                        <ThemeProvider theme={() => dataStore.theme}>
                            <Fragment>
                                {
                                    dataStore.config.showFundTypes && (
                                        <FundTypeTabs />
                                    )
                                }
                                <Wrapper
                                    {...dataStore.theme.Wrapper}
                                >
                                    {
                                        dataStore.isLoading && (
                                            <Fragment>
                                                <LoadingOverlay>
                                                    <Spinner
                                                        color="primary"
                                                        iconSize="lg"
                                                        {...dataStore.theme.LoadingSpinner}
                                                    />
                                                </LoadingOverlay>
                                                <DocDone />
                                            </Fragment>
                                        )
                                    }
                                    {
                                        dataStore.config.filtersTop
                                            ? (
                                                <MediaQuery
                                                    query={`(max-width: ${dataStore.theme.breakpoints.sm})`}
                                                >
                                                    {
                                                        matches => matches && (
                                                            <Filters
                                                                isMobile={matches}
                                                            />
                                                        )
                                                    }
                                                </MediaQuery>
                                            )
                                            : (
                                                <MediaQuery
                                                    query={`(max-width: ${dataStore.theme.breakpoints.md})`}
                                                >
                                                    {
                                                        matches => (
                                                            <Filters
                                                                isMobile={matches}
                                                            />
                                                        )
                                                    }
                                                </MediaQuery>
                                            )
                                    }
                                    <Main
                                        {...dataStore.theme.Main}
                                        margin={
                                            dataStore.config.filtersTop
                                                ? '0'
                                                : get(dataStore, 'theme.Main.margin', ["0", "0", "0 0 0 240px"])
                                        }
                                    >
                                        <Header />
                                        {
                                            dataStore.config.filtersTop && (
                                                <MediaQuery
                                                    query={`(min-width: ${dataStore.theme.breakpoints.sm})`}
                                                >
                                                    {
                                                        matches => matches && (
                                                            <FiltersTop
                                                                isMobile={matches}
                                                            />
                                                        )
                                                    }
                                                </MediaQuery>
                                            )
                                        }
                                        {
                                            dataStore.config.showModules && (
                                                <ModuleTabs />
                                            )
                                        }
                                        <Disclaimers 
                                            disclaimers={dataStore.headerDisclaimers}
                                            theme={{
                                                ...dataStore.theme,
                                                DisclaimersList: {...dataStore.theme.DisclaimersList, ...dataStore.theme.HeaderDisclaimersList},
                                                DisclaimerListItem: {
                                                    ...dataStore.theme.DisclaimerListItem, 
                                                    ...dataStore.theme.HeaderDisclaimerListItem
                                                },
                                                DisclaimerContent: {...dataStore.theme.DisclaimerContent, ...dataStore.theme.HeaderDisclaimerContent}
                                            }}
                                        />
                                        <FundsWrapper>
                                            {
                                                (dataStore.groupedFunds.length > 0)
                                                    ? <Fragment>
                                                        {
                                                            dataStore.groupedFunds.map((fund, index) => (
                                                                <Fund
                                                                    key={`${fund.fundName}-${index}`}
                                                                    grouped={fund.grouped}
                                                                    fundName={fund.fundName}
                                                                    shareClasses={fund.shareClasses}
                                                                />
                                                            ))
                                                        }
                                                        <LoadMoreButton
                                                            onClick={() => dataStore.getFunds(true)}
                                                            disabled={dataStore.endOfResults}
                                                            isLoading={dataStore.isLoadingMore}
                                                        />
                                                    </Fragment>
                                                    : <Fragment>
                                                        {
                                                            !dataStore.isLoading &&
                                                            <EmptyState>
                                                                <Translate>There are no funds matching this search</Translate>
                                                            </EmptyState>
                                                        }
                                                    </Fragment>
                                            }
                                        </FundsWrapper>
                                        <Disclaimers 
                                            disclaimers={dataStore.footerDisclaimers}
                                            theme={{
                                                ...dataStore.theme,
                                                DisclaimersList: {...dataStore.theme.DisclaimersList, ...dataStore.theme.FooterDisclaimersList},
                                                DisclaimerListItem: {
                                                    ...dataStore.theme.DisclaimerListItem, 
                                                    ...dataStore.theme.FooterDisclaimerListItem
                                                },
                                                DisclaimerContent: {...dataStore.theme.DisclaimerContent, ...dataStore.theme.FooterDisclaimerContent}
                                            }}
                                        />
                                    </Main>
                                </Wrapper>
                            </Fragment>
                        </ThemeProvider>
                    </Provider>
                )
                : (
                    <AppLoading />
                )
        );
    }
}

export default App;


// WEBPACK FOOTER //
// ./src/App.tsx